import { maskCpf, maskCnpj } from '../../../services/masks';

export type ParsedPropertyAddress = {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  fallback: boolean;
};

export const STATE_OPTIONS = [
  { key: 'AC', value: 'AC', text: 'AC - Acre' },
  { key: 'AL', value: 'AL', text: 'AL - Alagoas' },
  { key: 'AP', value: 'AP', text: 'AP - Amapá' },
  { key: 'AM', value: 'AM', text: 'AM - Amazonas' },
  { key: 'BA', value: 'BA', text: 'BA - Bahia' },
  { key: 'CE', value: 'CE', text: 'CE - Ceará' },
  { key: 'DF', value: 'DF', text: 'DF - Distrito Federal' },
  { key: 'ES', value: 'ES', text: 'ES - Espírito Santo' },
  { key: 'GO', value: 'GO', text: 'GO - Goiás' },
  { key: 'MA', value: 'MA', text: 'MA - Maranhão' },
  { key: 'MT', value: 'MT', text: 'MT - Mato Grosso' },
  { key: 'MS', value: 'MS', text: 'MS - Mato Grosso do Sul' },
  { key: 'MG', value: 'MG', text: 'MG - Minas Gerais' },
  { key: 'PA', value: 'PA', text: 'PA - Pará' },
  { key: 'PB', value: 'PB', text: 'PB - Paraíba' },
  { key: 'PR', value: 'PR', text: 'PR - Paraná' },
  { key: 'PE', value: 'PE', text: 'PE - Pernambuco' },
  { key: 'PI', value: 'PI', text: 'PI - Piauí' },
  { key: 'RJ', value: 'RJ', text: 'RJ - Rio de Janeiro' },
  { key: 'RN', value: 'RN', text: 'RN - Rio Grande do Norte' },
  { key: 'RS', value: 'RS', text: 'RS - Rio Grande do Sul' },
  { key: 'RO', value: 'RO', text: 'RO - Rondônia' },
  { key: 'RR', value: 'RR', text: 'RR - Roraima' },
  { key: 'SC', value: 'SC', text: 'SC - Santa Catarina' },
  { key: 'SP', value: 'SP', text: 'SP - São Paulo' },
  { key: 'SE', value: 'SE', text: 'SE - Sergipe' },
  { key: 'TO', value: 'TO', text: 'TO - Tocantins' }
];

export const SOURCE_TYPE_LABELS: Record<string, string> = {
  BOND_INSURANCE: 'Seguro Fiança',
  FIRE_INSURANCE: 'Seguro Incêndio',
  CAPITALIZATION: 'Capitalização',
  STANDALONE: 'Avulso'
};

export const formatDocument = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 11) {
    return maskCpf(cleaned);
  }
  return maskCnpj(cleaned);
};

export const parseMoneyToNumber = (value: string): number => {
  const cleaned = value.replace(/\D/g, '');
  return parseInt(cleaned, 10) / 100 || 0;
};

export const formatMoneyInput = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const number = parseInt(cleaned, 10) / 100 || 0;
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const formatMoneyFromNumber = (value?: number | string | null): string => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return formatMoneyInput('');
  }
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export const validateCpf = (cpf: string): boolean => {
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11 || /^(\d)\1+$/.test(cleaned)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cleaned.charAt(i), 10) * (10 - i);
  let rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;
  if (rest !== parseInt(cleaned.charAt(9), 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cleaned.charAt(i), 10) * (11 - i);
  rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) rest = 0;
  return rest === parseInt(cleaned.charAt(10), 10);
};

export const validateCnpj = (cnpj: string): boolean => {
  const cleaned = cnpj.replace(/\D/g, '');
  if (cleaned.length !== 14 || /^(\d)\1+$/.test(cleaned)) return false;
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += parseInt(cleaned.charAt(i), 10) * weights1[i];
  let rest = sum % 11;
  if (rest < 2) rest = 0;
  else rest = 11 - rest;
  if (rest !== parseInt(cleaned.charAt(12), 10)) return false;
  sum = 0;
  for (let i = 0; i < 13; i++) sum += parseInt(cleaned.charAt(i), 10) * weights2[i];
  rest = sum % 11;
  if (rest < 2) rest = 0;
  else rest = 11 - rest;
  return rest === parseInt(cleaned.charAt(13), 10);
};

export const validateDocument = (doc: string): boolean => {
  const cleaned = doc.replace(/\D/g, '');
  return cleaned.length === 11
    ? validateCpf(doc)
    : cleaned.length === 14
      ? validateCnpj(doc)
      : false;
};

export const buildFullAddress = (
  street: string,
  number: string,
  complement: string,
  neighborhood: string
): string => {
  return `${street}, ${number}${complement ? `, ${complement}` : ''}${
    neighborhood ? ` - ${neighborhood}` : ''
  }`;
};

export const parsePropertyAddress = (address?: string): ParsedPropertyAddress => {
  const value = (address || '').trim();
  if (!value) {
    return {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      fallback: false
    };
  }

  const match = value.match(/^(.+),\s*([^,-]+?)(?:,\s*(.+?))?\s*-\s*(.+)$/);
  if (!match) {
    return {
      street: value,
      number: '',
      complement: '',
      neighborhood: '',
      fallback: true
    };
  }

  return {
    street: match[1].trim(),
    number: match[2].trim(),
    complement: (match[3] || '').trim(),
    neighborhood: match[4].trim(),
    fallback: false
  };
};

export const toDateInputValue = (value?: string | null): string => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

export type ViaCepResult = {
  street: string;
  neighborhood: string;
  city: string;
  state: string;
};

export const fetchAddressByCep = async (cepDigits: string): Promise<ViaCepResult | null> => {
  const response = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`);
  const data = await response.json();
  if (data.erro) return null;
  return {
    street: data.logradouro || '',
    neighborhood: data.bairro || '',
    city: data.localidade || '',
    state: data.uf || ''
  };
};
